import { supabase } from '../lib/supabase';
import { logError } from '../lib/logError';
import { normalizePhone } from '../lib/phone';
import { blindarPontosPendentes } from './pontosConfig';

export const buscarComissaoPessoa = async (nome) => {
    const EMPTY = { id: null, comissao_agendamento: 0, comissao_venda: 0, comissao_venda_override: 0, comissao_indicacao: 0, cargo: '', cargo_id: null };
    if (!nome) return EMPTY;
    // `.limit(2)` em vez de `.single()`, de propósito. `.single()` devolve PGRST116 tanto para
    // ZERO linhas quanto para DUAS OU MAIS — e o `catch` de "não encontrado" engolia os dois
    // casos. Com um homônimo em `bd_pessoas`, a busca voltava EMPTY, a taxa ficava 0 e a venda
    // era gravada com comissão zero, sem erro e sem log. Duas linhas bastam para distinguir
    // "não existe" de "existe mais de um"; não precisa trazer a tabela.
    const { data, error } = await supabase
        .from('bd_pessoas')
        .select('id, cargo, cargo_id, comissao_agendamento, comissao_venda, comissao_indicacao, bd_cargos!cargo_id(id, nivel, comissao_agendamento_tipo, comissao_agendamento_valor, comissao_venda_tipo, comissao_venda_valor, comissao_venda_override_valor, comissao_indicacao_tipo, comissao_indicacao_valor)')
        .eq('nome', nome)
        .limit(2);
    if (error) {
        logError(error, { fn: 'buscarComissaoPessoa', nome });
        throw error;
    }
    const linhas = data ?? [];
    if (linhas.length === 0) return EMPTY;
    if (linhas.length > 1) {
        // Lança ANTES de qualquer escrita: `submitVenda`/`submitVendaTicket` chamam isto no
        // `Promise.all` inicial, então nada fica órfão. Corrigir o cadastro é a única saída —
        // escolher uma das linhas seria chutar de quem é a comissão.
        const err = new Error(`Há ${linhas.length} cadastros com o nome "${nome}" em bd_pessoas. Corrija o cadastro (Pessoas) antes de registrar a venda.`);
        err.code = 'PESSOA_DUPLICADA';
        logError(err, { fn: 'buscarComissaoPessoa', nome, quantidade: linhas.length });
        throw err;
    }
    const pessoa = linhas[0];

    // Se cargo_id resolvido → taxas vêm do cargo (source of truth)
    const c = pessoa.bd_cargos;
    if (c) {
        return {
            id: pessoa.id,
            cargo: pessoa.cargo,
            cargo_id: pessoa.cargo_id,
            // Nível do cargo: decide se a venda recebe gestão (Closer N3 não é liderado).
            nivel: c.nivel ?? null,
            comissao_agendamento: c.comissao_agendamento_valor ?? 0,
            comissao_venda:       c.comissao_venda_valor       ?? 0,
            // Taxa sobre venda fechada por um LIDERADO. Null no cargo → cai na taxa de venda
            // própria, que é o comportamento anterior à separação das duas taxas.
            comissao_venda_override: c.comissao_venda_override_valor ?? c.comissao_venda_valor ?? 0,
            comissao_indicacao:   c.comissao_indicacao_valor   ?? 0,
        };
    }

    // Fallback: colunas legadas de bd_pessoas (durante transição — antes de backfill cargo_id)
    return {
        id: pessoa.id,
        cargo: pessoa.cargo,
        cargo_id: null,
        nivel: null,
        comissao_agendamento: pessoa.comissao_agendamento ?? 0,
        comissao_venda:       pessoa.comissao_venda       ?? 0,
        comissao_venda_override: pessoa.comissao_venda    ?? 0,
        comissao_indicacao:   pessoa.comissao_indicacao   ?? 0,
    };
};

/**
 * Gestor comercial ativo — quem recebe override sobre venda de liderado.
 *
 * Não existe coluna ligando pessoa → gestor em `bd_pessoas` (só `squad_leader`, e os líderes
 * cadastrados hoje são de squad, não da operação comercial). O critério é o cargo: quem tem
 * `comissao_venda_override_valor` definido no cargo e não está desligado.
 *
 * Devolve null quando não há nenhum — aí o override simplesmente não é lançado.
 */
export const buscarGestorComercial = async () => {
    const { data, error } = await supabase
        .from('bd_pessoas')
        .select('id, nome, cargo, cargo_id, bd_cargos!cargo_id(comissao_venda_override_valor)')
        .eq('desligado', false);

    // Se a coluna de override ainda não existe (código deployado antes da migration), NÃO devolver
    // null — isso desligaria o override de todas as vendas em silêncio. Cai no critério por cargo.
    if (error) {
        logError(error, { fn: 'buscarGestorComercial/override' });
        const { data: alt, error: eAlt } = await supabase
            .from('bd_pessoas')
            .select('id, nome, cargo, cargo_id')
            .eq('desligado', false)
            .eq('cargo', 'Gestor Comercial');
        if (eAlt) { logError(eAlt, { fn: 'buscarGestorComercial/fallback' }); return null; }
        return (alt || [])[0] ?? null;
    }

    const gestores = (data || []).filter((p) => (p.bd_cargos?.comissao_venda_override_valor ?? 0) > 0);
    if (gestores.length === 0) return null;
    if (gestores.length > 1) {
        console.warn('[buscarGestorComercial] mais de um cargo com override; usando o primeiro:', gestores.map((g) => g.nome));
    }
    return gestores[0];
};

/**
 * Calcula mes_pagamento: data_base + 1 mês, dia 15.
 * Retorna string YYYY-MM-DD.
 *
 * Usa aritmética explícita de ano/mês para evitar overflow do setMonth() do JS:
 * ex. 31/mar → setMonth(3) em dia 31 avança para 01/mai em vez de 15/abr.
 */
export const calcularMesPagamento = (dataBaseString) => {
    const dataBase = dataBaseString ? new Date(dataBaseString + 'T12:00:00') : new Date();
    const ano = dataBase.getFullYear();
    const mes = dataBase.getMonth() + 2; // getMonth() é 0-indexed; +1 mês → +2
    const anoFinal = mes > 12 ? ano + 1 : ano;
    const mesFinal = mes > 12 ? 1 : mes;
    return `${anoFinal}-${String(mesFinal).padStart(2, '0')}-15`;
};

/**
 * Recalcula o `pontos_pendentes` de uma venda de closer aqui, na última porta antes do banco.
 *
 * `submitVenda` (`api.js:568`) e `submitVendaTicket` (`api.js:837`) montam esse objeto com
 * `w.closer[produto]?.[paymentCat] ?? 0`. Todo rótulo de produto sem palavra-chave própria cai
 * no último item de `produtos_config` — hoje `treinamentos_orbion`, que não tem tabela de pesos
 * — e o `?? 0` grava 0 sem erro, sem log e sem nada que distinga esse 0 de um 0 escolhido: são
 * 10 das 43 linhas `venda_closer` vivas hoje. `blindarPontosPendentes` devolve o mesmo número
 * onde a matriz responde e carimba a linha onde ela não responde.
 *
 * Vive aqui, e não em `api.js`, porque `inserirComissao` é o funil das duas chamadas. O
 * `import()` é dinâmico porque `api.js` importa este módulo: estático fecharia um ciclo. O build
 * avisa que `api.js` é importado dos dois jeitos e por isso não vai para outro chunk — é o
 * resultado desejado: nada é baixado a mais, e o ciclo não existe.
 *
 * Nunca lança. Problema de config não pode derrubar o registro de uma venda — é a mesma regra
 * do fallback de vigência do desk (`api.js:86-93`): quem está preenchendo o formulário é uma
 * pessoa, e travar a tela atrapalha a operação. Sem config, o objeto passa intacto.
 */
async function blindarVendaCloser(pontosPendentes, contexto) {
    if (!pontosPendentes || pontosPendentes.acao !== 'venda_closer') return pontosPendentes;
    try {
        const { getProducts, getWeightsParaData } = await import('./api');
        // A data do PONTO, não a `dataBase` da parcela: é ela que `api.js` usou para resolver a
        // vigência, e uma parcela de mês seguinte resolveria para outra config.
        const [produtos, { config }] = await Promise.all([
            getProducts(),
            getWeightsParaData(pontosPendentes.data),
        ]);
        const { pendentes, intervencao } = blindarPontosPendentes(pontosPendentes, { produtos, closer: config?.closer });
        if (intervencao) {
            await logError(
                new Error(`[venda_closer] ${intervencao.tipo}: "${intervencao.rotulo}" → ${intervencao.slug} / ${intervencao.condicao} (${intervencao.motivo ?? '—'})`),
                { fn: 'inserirComissao/blindarVendaCloser', severity: 'warning', ...contexto, ...intervencao },
            );
        }
        return pendentes;
    } catch (err) {
        await logError(err, { fn: 'inserirComissao/blindarVendaCloser', ...contexto });
        return pontosPendentes;
    }
}

/**
 * Insere uma linha de comissão em bd_comissoes_pendentes.
 * Aprovação via painel-de-comissoes move para bd_comissoes e materializa bd_pontos.
 *
 * ⛔ Ganho 0 COM ponto ainda gera linha — e é de propósito (2026-08-27).
 *
 * O ponto de uma venda não existe sozinho no desk: ele viaja como campo JSON
 * (`pontosPendentes`) dentro da linha de comissão. Quem o materializa em `bd_pontos` é a RPC
 * `approve_venda`, do repo `painel-de-comissoes`, e ela varre as comissões da venda com
 * `WHERE c.pontos_pendentes IS NOT NULL` — sem olhar `ganho`. Ou seja: sem linha de comissão,
 * não há onde o ponto viajar, e a RPC nunca o vê.
 *
 * O guard antigo era `if (!ganho || ganho <= 0) return;` na PRIMEIRA instrução. Quando a taxa da
 * pessoa é 0 (cargo sem `comissao_venda_valor`, produto com `customComissoes` zerado), o closer
 * perdia a comissão — o que está certo — E o ponto junto, em silêncio, sem log e sem throw,
 * enquanto a tela dizia "Venda registrada e encaminhada para aprovação". Ganho 0 é decisão de
 * remuneração; ponto 0 é dado de gamificação apagado sem ninguém escolher.
 *
 * Agora são quatro ramos:
 *  - sem `pessoa` e ganho > 0 → LANÇA, depois de logar `comissao-sem-pessoa` (severity error).
 *  - sem `pessoa` e ganho <= 0 → não insere, loga `comissao-sem-pessoa` (warning) e devolve.
 *  - ganho <= 0 SEM ponto → não insere (nada a carregar) e loga `comissao-zero` (warning).
 *  - ganho <= 0 COM ponto → INSERE com ganho 0, para o ponto chegar à `approve_venda`, e loga.
 *
 * ⛔ Por que o ramo sem `pessoa` LANÇA quando há valor (2026-08-27).
 *
 * A versão anterior desta nota afirmava que `pessoa: null` era inalcançável. É FALSO, por duas
 * portas independentes:
 *  - `ghlVendasTickets.js:41` monta `closer_nome: nomePorGhlUserId.get(...) ?? null` — o closer
 *    sem linha em `ghl_pessoa_alias` vem NULO. O filtro de `:62` só esconde esse ticket de quem
 *    tem `role === 'closer'`; um gestor vê o ticket e o completa por `submitVendaTicket`.
 *  - o ganho não morre junto com o nome: `resolveCommissionPolicy` (`commissionRules.js:246-251`)
 *    soma `percentage_points` e empilha `fixedBonuses` SEM olhar `baseRate`. Com `baseRate = 0`,
 *    uma regra de pontos percentuais ainda produz `effectiveRate > 0`, e uma regra `fixed` gera
 *    bônus que `inserirBonusFixoDaVenda` (`api.js:383`) manda para cá com `ganho = bonus.amount`.
 *
 * Logo `!pessoa && ganho > 0` acontece. Até 2026-08-27 esse caso batia no `NOT NULL` de
 * `bd_comissoes_pendentes.pessoa` (`supabase/migration-2026-06-05-missing-tables.sql:56`),
 * devolvia 23502, e o `throw` subia por `submitVenda`/`submitVendaTicket`: o operador VIA o erro
 * e consertava o alias. Trocar isso por um `return` silencioso apaga dinheiro devido a alguém
 * DEPOIS de a venda e a receita já estarem gravadas — perda de folha invisível, que é pior que a
 * venda pendente órfã deixada pelo `throw` (essa aparece na tela, no log e com o nome do cliente).
 * Comissão COM valor e SEM destinatário é erro que precisa ser VISTO, não engolido. Sem valor não
 * há dinheiro a perder: loga e segue, para não travar o formulário por linha que não pagaria nada.
 *
 * Devolve `{ inserida, motivo? , ganho?, zero? }`. Os 7 pontos de chamada em `api.js` fazem
 * `await inserirComissao({...})` sem atribuir — o retorno é para teste e para quem vier depois.
 *
 * `primeira` existe só para o LOG de `comissao-zero`: ver a nota no ramo correspondente.
 */
export const inserirComissao = async ({ pessoa, pessoaId = null, tipo, cargo, cargoId = null, cliente, ganho, valorBase = null, dataBase, telefone, pontosPendentes = null, vendaId = null, primeira = true }) => {
    const ganhoNum = Number(ganho) || 0;
    const carregaPonto = pontosPendentes != null;

    if (!pessoa) {
        const comValor = ganhoNum > 0;
        const detalhe = `comissão de ${ganhoNum} em "${tipo}" para "${cliente}" sem pessoa identificada`;
        await logError(
            new Error(`[comissao-sem-pessoa] ${detalhe}${comValor ? ' — lançando: comissão com valor não some em silêncio' : ' — sem valor, linha descartada'}`),
            { fn: 'inserirComissao', codigo: 'comissao-sem-pessoa', severity: comValor ? 'error' : 'warning', tipo, cliente, vendaId, ganho: ganhoNum, temPonto: carregaPonto, lancou: comValor },
        );
        // Mesmo efeito prático do 23502 que a coluna NOT NULL dava antes: propaga para
        // `submitVendaTicket`/`submitVenda` e a tela mostra o erro em vez de dizer "sucesso".
        if (comValor) throw new Error(`[comissao-sem-pessoa] ${detalhe}`);
        return { inserida: false, motivo: 'sem-pessoa' };
    }

    if (ganhoNum <= 0) {
        // Só a PRIMEIRA parcela loga. As 2..N chegam com ganho 0 pelo mesmo motivo já registrado
        // (a taxa é constante no loop) e com `pontosPendentes: null` — logá-las custava um
        // round-trip de `log_app_error` cada, 39 numa venda 12x com taxa 0 para as três pessoas,
        // e ainda escrevia "sem ponto a carregar, nada gravado", que é falso: o ponto existe e
        // foi gravado pela primeira parcela. Quem lesse `app_error_events` perseguiria um fantasma.
        if (primeira) {
            await logError(
                new Error(`[comissao-zero] ganho 0 em "${tipo}" para ${pessoa}${carregaPonto ? ' — linha gravada mesmo assim para o ponto chegar à aprovação' : ' — sem ponto a carregar, nada gravado'}`),
                { fn: 'inserirComissao', codigo: 'comissao-zero', severity: 'warning', pessoa, pessoaId, tipo, cargo, cargoId, cliente, vendaId, valorBase, temPonto: carregaPonto },
            );
        }
        // Sem ponto para carregar, ganho 0 continua não virando linha — só que agora com log.
        if (!carregaPonto) return { inserida: false, motivo: 'zero-sem-ponto' };
    }

    const pendentesBlindados = await blindarVendaCloser(pontosPendentes, { pessoa, cliente, vendaId });
    const mesPagamento = calcularMesPagamento(dataBase);
    const { error } = await supabase.from('bd_comissoes_pendentes').insert({
        pessoa,
        pessoa_id: pessoaId,
        tipo,
        cargo,
        cargo_id: cargoId,
        cliente,
        ganho: ganhoNum,
        valor_base: valorBase,
        data_base: dataBase || null,
        mes_pagamento: mesPagamento,
        telefone: normalizePhone(telefone),
        created_by: 'desk',
        pontos_pendentes: pendentesBlindados,
        venda_id: vendaId,
    });
    if (error) {
        logError(error, { fn: 'inserirComissao', pessoa, cliente });
        throw error;
    }
    return { inserida: true, ganho: ganhoNum, zero: ganhoNum <= 0 };
};
